import { useEffect } from "react";
import { Link as RouterLink } from "react-router";
import {
  Box,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import {
  CategoryOutlined,
  DescriptionOutlined,
  GavelOutlined,
  ImportExportOutlined,
  LabelOutlined,
  NewspaperOutlined,
  ReportProblemOutlined,
  AccountTreeOutlined,
} from "@mui/icons-material";
import { useBreadcrumb } from "@accredo/fe-shared/contexts/BreadcrumbContext.jsx";
import { useAbility } from "../../../hooks/permissions/useAbility.jsx";

const SECTIONS = [
  {
    title: "Import",
    items: [
      {
        label: "Import",
        description:
          "Connectoren, Quellen, Mappings, Jobs und Kunden-Einstellungen verwalten",
        path: "/admin/imports/jobs",
        icon: <ImportExportOutlined />,
      },
    ],
  },
  {
    title: "Sparten & Schadenformulare",
    items: [
      {
        label: "Sparten",
        description:
          "Versicherungssparten und zugehörige Schadenformulare einsehen",
        path: "/admin/sparten",
        icon: <CategoryOutlined />,
        can: ["Sparte", "viewAny"],
      },
      {
        label: "Schadenformulare",
        description:
          "Schadenformulare, deren Definitionen und Section-Templates verwalten",
        path: "/admin/claim-forms",
        icon: <DescriptionOutlined />,
        can: ["ClaimForm", "viewAny"],
      },
      {
        label: "Schadenarten & Schadenursachen",
        description: "Schadenursachen und die zugehörige Schadenart einsehen",
        path: "/admin/schaden-ursachen",
        icon: <ReportProblemOutlined />,
        can: ["SchadenUrsache", "viewAny"],
      },
    ],
  },
  {
    title: "Dokumente",
    items: [
      {
        label: "Dokumenttypen",
        description:
          "Dokumenttypen und die Zuordnung von OpenViva-Kategorien verwalten",
        path: "/admin/document-types",
        icon: <LabelOutlined />,
        can: ["DocumentType", "viewAny"],
      },
    ],
  },
  {
    title: "Workflow",
    items: [
      {
        label: "Status-Workflow",
        description:
          "Übersicht, was bei einem Statuswechsel passiert und wer was bekommt, inkl. Erinnerungen bearbeiten",
        path: "/admin/workflow",
        icon: <AccountTreeOutlined />,
        can: ["ClaimWorkflow", "viewAny"],
      },
    ],
  },
  {
    title: "Inhalte",
    items: [
      {
        label: "Was ist neu?",
        description: "Changelog-Beiträge erstellen und verwalten",
        path: "/admin/blog",
        icon: <NewspaperOutlined />,
        can: ["Blog", "update"],
      },
    ],
  },
  {
    title: "Rechtliches",
    items: [
      {
        label: "Einwilligungsdokumente",
        description:
          "Versionen von Datenschutzerklärung und Nutzungsbedingungen verwalten",
        path: "/admin/consent-documents",
        icon: <GavelOutlined />,
      },
    ],
  },
];

export default function AdminSettingsPage() {
  const { setBreadcrumbs } = useBreadcrumb();
  const { can } = useAbility();

  useEffect(() => {
    setBreadcrumbs([
      { name: "Home", path: "/home" },
      { name: "Einstellungen", path: "/admin/settings" },
    ]);
  }, [setBreadcrumbs]);

  const visibleSections = SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => !item.can || can(...item.can)),
  })).filter((section) => section.items.length > 0);

  return (
    <>
      <Typography variant="h5" mb={3}>
        Einstellungen
      </Typography>

      <Box display="flex" flexDirection="column" gap={3}>
        {visibleSections.map((section) => (
          <Box key={section.title}>
            <Typography variant="subtitle2" color="text.secondary" mb={1}>
              {section.title}
            </Typography>
            <Paper variant="outlined">
              <List disablePadding>
                {section.items.map((item, i) => (
                  <Box key={item.path}>
                    {i > 0 && <Divider />}
                    <ListItemButton component={RouterLink} to={item.path}>
                      {item.icon && (
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          {item.icon}
                        </ListItemIcon>
                      )}
                      <ListItemText
                        primary={item.label}
                        secondary={item.description}
                        primaryTypographyProps={{ fontWeight: 500 }}
                      />
                    </ListItemButton>
                  </Box>
                ))}
              </List>
            </Paper>
          </Box>
        ))}
      </Box>
    </>
  );
}
